// src/pages/guides/InternalVibrationsGuide.js
//
// Hosted lead-magnet guide. People who opt in on the internal-vibrations blog
// post are emailed a link here (not a PDF attachment) — better inbox
// deliverability, better on mobile, trackable, and it can carry a next step.
// A print button lets anyone who wants the file save it as PDF from the browser.
//
// Responsible-content note: this is general education + reassurance, never
// individualized medical advice. No diagnosis, no ruling-out, no dosing, and it
// consistently points readers to a doctor. Reviewed line-by-line for that.

import React from 'react';
import { Helmet } from 'react-helmet';

const GUIDE_CSS = `
.iv-guide {
  --paper: #f3f6f4; --card: #ffffff; --ink: #17242b; --soft: #52646c;
  --faint: #8497a0; --anchor: #0a4b5e; --sage: #5f8d78; --sage-deep: #3f6b57;
  --clay: #a4563f; --line: #dde5e1;
  --shadow: 0 1px 2px rgba(20,40,50,.05), 0 8px 30px rgba(20,40,50,.06);
  --measure: 63ch;
  background: var(--paper); color: var(--ink);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, Roboto, sans-serif;
  font-size: 18px; line-height: 1.66; -webkit-font-smoothing: antialiased;
}
.iv-guide * { box-sizing: border-box; }
.iv-serif { font-family: "Iowan Old Style", "Palatino Linotype", Palatino, "Book Antiqua", Georgia, serif; }
.iv-wrap { max-width: 720px; margin: 0 auto; padding: clamp(28px, 5vw, 56px) 22px 72px; }

.iv-cover { padding-bottom: 30px; border-bottom: 1px solid var(--line); margin-bottom: 8px; }
.iv-eyebrow { font-size: 12px; letter-spacing: .16em; text-transform: uppercase; color: var(--sage-deep); font-weight: 600; margin: 0 0 18px; }
.iv-guide h1 { font-size: clamp(2rem, 6vw, 2.9rem); line-height: 1.08; margin: 0 0 18px; letter-spacing: -.01em; text-wrap: balance; font-weight: 600; color: var(--ink); }
.iv-standfirst { font-size: clamp(1.08rem, 2.6vw, 1.28rem); line-height: 1.5; color: var(--soft); max-width: 36ch; margin: 0; text-wrap: balance; }
.iv-source { margin-top: 22px; font-size: 13.5px; color: var(--faint); }
.iv-source b { color: var(--anchor); font-weight: 600; }

.iv-actions { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 24px; }
.iv-btn { display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: 1px solid var(--line); background: var(--card); color: var(--anchor); text-decoration: none; }
.iv-btn:hover { border-color: var(--sage); }

.iv-contents { max-width: var(--measure); margin: 32px 0 8px; padding: 22px 24px; background: var(--card); border: 1px solid var(--line); border-radius: 10px; box-shadow: var(--shadow); }
.iv-contents p { font-size: 12px; letter-spacing: .14em; text-transform: uppercase; color: var(--sage-deep); font-weight: 700; margin: 0 0 12px; }
.iv-contents ol { margin: 0; padding: 0; list-style: none; counter-reset: c; display: grid; gap: 7px; }
.iv-contents li { counter-increment: c; padding-left: 30px; position: relative; color: var(--soft); font-size: 15.5px; }
.iv-contents li::before { content: counter(c); position: absolute; left: 0; top: 0; font-variant-numeric: tabular-nums; color: var(--sage); font-weight: 700; font-size: 13px; }

.iv-guide section { max-width: var(--measure); }
.iv-guide h2 { font-size: clamp(1.4rem, 3.5vw, 1.74rem); line-height: 1.18; margin: 48px 0 14px; letter-spacing: -.01em; font-weight: 600; text-wrap: balance; color: var(--ink); }
.iv-guide h3.iv-sub { font-size: 1.16rem; font-weight: 650; margin: 26px 0 6px; color: var(--ink); }
.iv-guide p { margin: 0 0 18px; }
.iv-guide strong { color: var(--ink); font-weight: 650; }
.iv-guide em { color: var(--anchor); font-style: italic; }

.iv-leadin::first-letter { font-family: "Iowan Old Style", Palatino, Georgia, serif; font-size: 3.1em; line-height: .8; float: left; padding: 6px 10px 0 0; color: var(--sage-deep); }

.iv-note { background: var(--card); border: 1px solid var(--line); border-left: 3px solid var(--sage); border-radius: 10px; padding: 20px 22px; margin: 26px 0; box-shadow: var(--shadow); }
.iv-note .iv-label { font-size: 12px; letter-spacing: .14em; text-transform: uppercase; color: var(--sage-deep); font-weight: 700; margin: 0 0 8px; }
.iv-note p:last-child { margin-bottom: 0; }

.iv-pull { max-width: var(--measure); margin: 42px 0; padding: 4px 0 4px 26px; border-left: 3px solid var(--sage); font-size: clamp(1.3rem, 3.2vw, 1.55rem); line-height: 1.36; color: var(--ink); font-style: italic; }

.iv-helps { list-style: none; padding: 0; margin: 8px 0 20px; display: grid; gap: 16px; }
.iv-helps li { padding-left: 30px; position: relative; line-height: 1.55; }
.iv-helps li::before { content: ""; position: absolute; left: 4px; top: .58em; width: 9px; height: 9px; border-radius: 50%; background: var(--sage); box-shadow: 0 0 0 4px color-mix(in srgb, var(--sage) 18%, transparent); }
.iv-helps li b { display: block; margin-bottom: 2px; color: var(--ink); }
.iv-helps li span { color: var(--soft); }

.iv-faq { max-width: var(--measure); margin-top: 10px; display: grid; gap: 2px; }
.iv-faq details { border-bottom: 1px solid var(--line); padding: 4px 0; }
.iv-faq summary { cursor: pointer; padding: 14px 2px; font-weight: 650; font-size: 1.05rem; color: var(--ink); list-style: none; display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.iv-faq summary::-webkit-details-marker { display: none; }
.iv-faq summary::after { content: "+"; color: var(--sage); font-size: 1.5rem; font-weight: 400; line-height: 1; flex: none; }
.iv-faq details[open] summary::after { content: "\\2013"; }
.iv-faq details[open] summary { color: var(--sage-deep); }
.iv-faq .iv-a { padding: 0 2px 16px; color: var(--soft); }
.iv-faq .iv-a p { margin: 0; }

.iv-flag { background: color-mix(in srgb, var(--clay) 6%, var(--card)); border: 1px solid color-mix(in srgb, var(--clay) 30%, var(--line)); border-radius: 10px; padding: 22px 24px; margin: 30px 0; max-width: var(--measure); }
.iv-flag h3 { margin: 0 0 6px; font-size: 1.14rem; color: var(--clay); font-weight: 650; display: flex; align-items: center; gap: 10px; }
.iv-flag h3 svg { flex: none; }
.iv-flag p { color: var(--soft); }
.iv-flag ul { margin: 12px 0 0; padding-left: 20px; color: var(--soft); }
.iv-flag li { margin-bottom: 7px; }
.iv-flag li::marker { color: var(--clay); }

.iv-rule { border: 0; border-top: 1px solid var(--line); margin: 46px 0; max-width: var(--measure); }
.iv-closer { max-width: var(--measure); margin-top: 38px; }
.iv-closer .iv-closer-h { font-size: 1.32rem; margin-bottom: 10px; color: var(--ink); }
.iv-closer p { font-size: 1.06rem; }
.iv-sign { font-style: italic; color: var(--soft); }

.iv-next { max-width: var(--measure); margin-top: 44px; padding: 26px; border-radius: 12px; background: var(--card); border: 1px solid var(--line); box-shadow: var(--shadow); }
.iv-next h3 { margin: 0 0 8px; font-size: 1.2rem; color: var(--ink); }
.iv-next p { margin: 0 0 16px; color: var(--soft); }

.iv-disclaimer { max-width: var(--measure); margin-top: 44px; padding-top: 20px; border-top: 1px solid var(--line); font-size: 12.5px; line-height: 1.6; color: var(--faint); }
.iv-guide a { color: var(--anchor); }

@media print {
  nav, .navbar, footer, .iv-actions, .iv-next { display: none !important; }
  body * { visibility: hidden; }
  .iv-guide, .iv-guide * { visibility: visible; }
  .iv-guide { position: absolute; left: 0; top: 0; width: 100%; background: #fff; font-size: 12pt; }
  .iv-wrap { max-width: none; padding: 0 24px; }
  .iv-note, .iv-flag, .iv-contents { box-shadow: none; break-inside: avoid; }
  .iv-faq details .iv-a { display: block !important; }
  .iv-faq summary::after { display: none; }
  .iv-guide h2 { break-after: avoid; }
}
`;

function InternalVibrationsGuide() {
  return (
    <div className="iv-guide">
      <Helmet>
        <title>Understanding Internal Vibrations — A Calm Guide | BFS</title>
        <meta
          name="description"
          content="Why your body buzzes when nothing is moving — what internal vibrations usually mean, what helps, and when to see a doctor. A calm, honest guide."
        />
      </Helmet>
      <style>{GUIDE_CSS}</style>

      <div className="iv-wrap">
        <header className="iv-cover">
          <p className="iv-eyebrow">A calm guide · Benign Fasciculation Syndrome</p>
          <h1 className="iv-serif">Understanding Internal Vibrations</h1>
          <p className="iv-standfirst">
            Why your body buzzes when nothing is moving — what it usually means, and how to settle it.
          </p>
          <p className="iv-source">From the team at <b>BenignFasciculationSyndrome.org</b></p>
          <div className="iv-actions">
            <button type="button" className="iv-btn" onClick={() => window.print()}>
              🖨️ Print / Save as PDF
            </button>
          </div>
        </header>

        <nav className="iv-contents" aria-label="What's inside">
          <p>What's inside</p>
          <ol>
            <li>What internal vibrations feel like</li>
            <li>What they usually are</li>
            <li>The worry loop that keeps them going</li>
            <li>What turns the volume up</li>
            <li>The connection to BFS</li>
            <li>Why it's worse at night and at rest</li>
            <li>What actually helps — a toolkit</li>
            <li>Building a calmer relationship with the sensation</li>
            <li>What to expect over time</li>
            <li>Common questions</li>
            <li>When to check in with a doctor</li>
          </ol>
        </nav>

        <section>
          <p style={{ color: 'var(--soft)', fontSize: '0.95rem', fontStyle: 'italic', marginTop: '28px' }}>
            A note before we begin: this guide offers general information and reassurance, not
            medical advice, and it can't speak to your particular situation. Think of it as a calm
            starting point — and see the section near the end on when a doctor's input is worth getting.
          </p>
          <p className="iv-leadin">
            You feel it before you can explain it — a fine humming or buzzing deep inside, often in
            the chest, the legs, or seemingly everywhere at once. You look down and nothing is moving.
            It tends to arrive when you're still, especially at night, and it can be genuinely
            frightening. If you've spent late nights searching for what it means, please know two
            things right away: you are not imagining it, and you are very far from alone.
          </p>
          <p>
            Internal vibrations are one of the most common experiences people bring to us, and also
            one of the most misunderstood — including by the people living with them. Because you can
            feel the sensation so clearly but can't see anything, the mind rushes to fill the gap,
            often with the worst explanation it can find. This guide is here to slow that rush down and
            walk you, calmly and honestly, through what's usually going on and what genuinely helps.
          </p>
          <p>
            We'll be straight with you throughout. Most of the time internal vibrations are benign —
            but "most of the time" is not "always," so we'll also be clear about the handful of signs
            that deserve a doctor's attention. Reassurance you can trust has to include that honesty.
          </p>
        </section>

        <section>
          <h2 className="iv-serif">What internal vibrations feel like</h2>
          <p>
            People reach for different words, and you may recognise your own experience in several of
            them. There's no single "correct" version — the variety is part of the picture.
          </p>
          <ul className="iv-helps">
            <li><b>Like a phone buzzing inside you</b><span>A short, fine vibration — in the thigh, calf, chest, or abdomen — so convincing that people check their pocket for a phone that isn't there.</span></li>
            <li><b>Like an idling engine</b><span>A low, continuous hum or tremor, as though a motor were running somewhere inside the body, often felt more than pinned to any single spot.</span></li>
            <li><b>A whole-body trembling with nothing to see</b><span>An internal shakiness — especially first thing in the morning or when tired — while your hands, held out, stay perfectly still.</span></li>
            <li><b>A rush or wave</b><span>A brief surge that travels through the body, sometimes with a jolt as you're falling asleep.</span></li>
          </ul>
          <p>
            The common thread is the mismatch we mentioned: a <em>strong internal sensation with
            little or no visible movement</em>. Hold your hands out and they don't shake. Ask someone
            to look and there's nothing to point to. That gap between what you feel and what shows is
            unsettling — and, as we'll see, it's also a clue.
          </p>
        </section>

        <section>
          <h2 className="iv-serif">What internal vibrations usually are</h2>
          <p>
            For most people who experience them without other symptoms, internal vibrations come from
            an <strong>over-alert nervous system</strong> — a body that has been running in a low-grade
            "high alert" state for a while, often without the person realising it.
          </p>
          <p>
            Your nervous system has two broad gears. One — the <em>sympathetic</em>, or "fight or
            flight" — speeds you up, tenses muscles, and sharpens your senses for action. The other —
            the <em>parasympathetic</em>, or "rest and digest" — slows you down and lets you recover.
            These are meant to trade off through the day. But under sustained stress, worry, or poor
            sleep, the "alert" gear can get stuck partly engaged. Muscles stay subtly braced, adrenaline
            stays slightly elevated, and the body develops a faint, fizzing background tension. That
            tension is often felt as an internal vibration.
          </p>
          <p>
            There's a second piece: an alert nervous system doesn't just <em>produce</em> more of these
            sensations, it <em>amplifies</em> them. When you're keyed up, the brain turns up the volume
            on internal signals it would normally filter out entirely. So the same faint buzz that a
            relaxed person would never notice becomes loud, insistent, and impossible to ignore.
          </p>
          <div className="iv-note">
            <p className="iv-label">This part matters most</p>
            <p>
              A benign internal vibration is a <em>sensation</em>, not a loss of function. It doesn't
              take away your strength, your coordination, or your ability to do the things you could do
              yesterday — it simply feels alarming. Throughout this guide, hold on to the difference
              between <strong>how something feels</strong> and <strong>what your body can still
              do</strong>. It's one of the most useful distinctions you have.
            </p>
          </div>
        </section>

        <section>
          <h2 className="iv-serif">The worry loop that keeps them going</h2>
          <p>
            Here is the part almost nobody explains, and it may be the most useful thing in this guide.
            Internal vibrations, in an anxious nervous system, tend to run in a loop:
          </p>
          <p>
            You <strong>notice</strong> the sensation. You <strong>worry</strong> about what it might
            mean. The worry itself is a stressor, so your nervous system raises its alertness a notch.
            Higher alertness <strong>amplifies</strong> the sensation and can generate more of it. The
            stronger sensation gives you more to worry about — and around it goes.
          </p>
          <p>
            None of this requires anything to be medically wrong. The loop can sustain itself on worry
            alone. That's genuinely good news, because it means the loop can also be <em>interrupted</em>
            — not by forcing the sensation to stop, but by lowering the alertness that feeds it and
            loosening the grip of the worry. Much of the toolkit further down is aimed squarely at this loop.
          </p>
        </section>

        <section>
          <h2 className="iv-serif">What turns the volume up</h2>
          <p>
            Internal vibrations rarely have a single cause. More often, a few ordinary things stack up
            until the nervous system tips into that buzzing state. You may recognise several of these
            acting together:
          </p>
          <ul className="iv-helps">
            <li><b>Stress and adrenaline</b><span>The most common driver by far. A body braced against threat hums — even when the "threat" is worry about the hum itself.</span></li>
            <li><b>Poor or broken sleep</b><span>Sleep is when the nervous system resets. Short-change it, or sleep badly, and everything runs hotter the next day.</span></li>
            <li><b>Caffeine and stimulants</b><span>Coffee, energy drinks, pre-workout, and some medications nudge an already-alert system further along. The effect can linger for hours.</span></li>
            <li><b>Alcohol and its after-effects</b><span>Alcohol disrupts sleep and, as it leaves the body, can leave the nervous system rebounding into a more jittery, buzzy state.</span></li>
            <li><b>Blood-sugar swings</b><span>Long gaps between meals or very sugary snacks can trigger adrenaline as your body corrects — and adrenaline is a classic buzz-maker.</span></li>
            <li><b>Dehydration and low electrolytes</b><span>Magnesium, potassium, sodium, and simple hydration all affect how calm your nerves and muscles feel.</span></li>
            <li><b>Being run-down or post-viral</b><span>After an infection, or during a stretch of exhaustion, the nervous system is often more reactive than usual for a while.</span></li>
          </ul>
          <p>
            Notice how ordinary this list is. There's nothing exotic here — which is exactly the point.
            The most common recipe for internal vibrations is simply modern life turned up a little too
            high for a little too long.
          </p>
        </section>

        <section>
          <h2 className="iv-serif">The connection to BFS</h2>
          <p>
            If you've landed here, you may already know the term <strong>Benign Fasciculation
            Syndrome</strong> — a harmless condition in which muscles twitch, flicker, and buzz without
            any underlying disease. Internal vibrations sit naturally alongside it. Many people with BFS
            describe both the visible flickers <em>and</em> the felt-but-unseen buzzing, and the two
            share the same root: a nervous system that has become over-excitable and over-attentive to
            its own signals.
          </p>
          <p>
            The word that matters in that name is <em>benign</em>. BFS, by definition, is not a damaging
            condition — it doesn't progress to something worse, and the twitching and buzzing, however
            loud, are not eating away at your muscles or nerves. What they can do is capture your
            attention completely, which is why so much of managing BFS is really about calming the
            nervous system and gently rebuilding trust in a body that feels like it's misbehaving.
          </p>
        </section>

        <section>
          <h2 className="iv-serif">Why it's worse at night and at rest</h2>
          <p>
            Almost everyone notices the same thing: the buzzing is loudest when they lie down, and
            quietest when they're absorbed in something. There are two clean reasons for this, and both
            are reassuring once you see them.
          </p>
          <h3 className="iv-sub">There's less competing for your attention</h3>
          <p>
            During the day, a hundred small things pull at your focus — work, conversations, screens,
            tasks. Each one crowds the sensation out. At night, or in any still moment, those
            distractions fall away, and the only thing left to notice is your own body. The vibration
            hasn't necessarily grown stronger; the competition has simply gone quiet, so it fills the
            whole stage.
          </p>
          <h3 className="iv-sub">Stillness invites the worry loop in</h3>
          <p>
            Rest is also when the mind wanders, and a worried mind tends to wander toward the thing it
            fears. So you lie down, you notice the buzz, the worry starts, alertness rises, and the loop
            from earlier spins up at exactly the moment you're trying to sleep. This is why "it's worst
            when I'm trying to rest" is such a common — and such a benign — pattern. A symptom driven by
            relaxation-turned-to-vigilance behaves very differently from one driven by disease.
          </p>
        </section>

        <p className="iv-pull iv-serif">
          The sensation is real. The story your fear tells about it usually isn't. Most of the work is
          learning to keep those two apart.
        </p>

        <section>
          <h2 className="iv-serif">What actually helps — a toolkit</h2>
          <p>
            Because the root is usually an over-alert nervous system, the things that help are the
            things that <strong>tell your body it's safe to stand down</strong>. None of these are
            dramatic — that's rather the point. They work through repetition, not intensity. Pick two or
            three to begin with rather than trying everything at once.
          </p>
          <h3 className="iv-sub">Lengthen your exhale</h3>
          <p>
            Breathe out for longer than you breathe in — for example, in for a count of four, out for a
            count of six — for a few unhurried minutes. A long, slow exhale is one of the few direct
            switches you have into the body's "rest" gear; it gently signals the nervous system that the
            emergency is over. Done regularly, not just in a panic, it lowers the baseline the vibrations
            run on.
          </p>
          <h3 className="iv-sub">Protect your sleep</h3>
          <p>
            Since poor sleep is such a reliable fuel, guarding it pays off more than almost anything. A
            consistent bedtime, a genuine wind-down away from screens, a cool dark room, and a gentle
            routine give your nervous system the nightly reset it's asking for. If the buzzing makes
            falling asleep hard, pair it with the breathing above rather than lying there monitoring it.
          </p>
          <h3 className="iv-sub">Ease off stimulants</h3>
          <p>
            Try trimming caffeine — especially after midday — and see whether your evenings grow quieter
            over a week or two. The same goes for alcohol before bed. You're not looking for perfection,
            just for less fuel on the fire.
          </p>
          <h3 className="iv-sub">Move gently, every day</h3>
          <p>
            Walking, stretching, swimming, light strength work — regular, moderate movement discharges
            the very tension that feeds the buzz, and it's one of the best-evidenced ways to lower
            background anxiety. It also does something quietly powerful: it proves to you, in your own
            body, that your strength is intact.
          </p>
          <h3 className="iv-sub">Steady your fuel and hydration</h3>
          <p>
            Aim for regular meals rather than long gaps followed by sugar, keep water consistent through
            the day, and eat a balanced diet with plenty of magnesium- and potassium-rich foods — leafy
            greens, nuts, seeds, beans, bananas. Calm, well-fuelled muscles and nerves buzz less.
          </p>
          <h3 className="iv-sub">Try a grounding technique when it spikes</h3>
          <p>
            When the sensation surges and your attention locks onto it, deliberately move your focus
            outward. One simple method: name five things you can see, four you can hear, three you can
            touch, two you can smell, one you can taste. It sounds almost too simple, but shifting
            attention outward is precisely what breaks the inward spiral of the worry loop.
          </p>
          <div className="iv-note">
            <p className="iv-label">One honest note on supplements</p>
            <p>
              People often ask about magnesium in particular. It's a reasonable thing to explore — but
              the right form and the right amount depend on you and on anything else you take, so that's
              a conversation for your doctor or pharmacist, not a number from a guide. We'd rather point
              you to the right person than guess.
            </p>
          </div>
        </section>

        <section>
          <h2 className="iv-serif">Building a calmer relationship with the sensation</h2>
          <p>
            There's an inner habit that quietly keeps internal vibrations going, and it deserves its own
            section because it's so common and so fixable: <strong>checking</strong>.
          </p>
          <p>
            When a sensation frightens us, it's natural to monitor it — to keep scanning, testing our
            strength, poking the muscle, or searching online one more time for reassurance. The trouble
            is that checking is a form of vigilance, and vigilance is exactly what turns the volume up.
            Each check briefly soothes and then deepens the groove of worry, so the relief never lasts
            and the habit grows.
          </p>
          <p>
            Gently loosening this is some of the most valuable work you can do. That doesn't mean forcing
            yourself to ignore your body or pretending you're not worried. It means, little by little,
            letting a buzz be present without immediately investigating it — noticing the urge to check
            and choosing, sometimes, not to. Attention is like water: the less you pour it onto the
            sensation, the more the sensation tends to recede.
          </p>
          <p>
            If health anxiety has taken a firm hold — if the searching and checking have started to run
            your days — please know that this is common, understandable, and very treatable, and that
            talking to a professional about it is a sign of good sense, not weakness.
          </p>
        </section>

        <section>
          <h2 className="iv-serif">What to expect over time</h2>
          <p>A few honest words about the road ahead, because not knowing what's normal is its own source of worry.</p>
          <p>
            Benign internal vibrations tend to <strong>wax and wane</strong>. They come in stretches —
            worse during stressful or sleep-deprived weeks, quieter during calmer ones — and they can
            move around the body, change character, or vanish for a while and return. This waxing and
            waning is characteristic of a benign, stress-linked pattern; it is not a sign that anything
            is progressing.
          </p>
          <p>
            Progress also tends not to be a straight line. Most people find the sensation loosens its
            grip gradually as sleep improves, stress eases, and the checking habit softens — with good
            days arriving before the good weeks do. A flare after a hard week doesn't erase that
            progress; it's just the nervous system responding to load, exactly as it's meant to.
            Patience, here, is not passive. Giving the calm habits time <em>is</em> the treatment.
          </p>
        </section>

        <section>
          <h2 className="iv-serif">Common questions</h2>
          <div className="iv-faq">
            <details>
              <summary>Does this mean I have ALS or MS?</summary>
              <div className="iv-a"><p>Internal vibrations on their own — without genuine, progressive muscle weakness, wasting, or trouble with swallowing or speech — are not a typical way that serious neurological disease announces itself. Those conditions are defined by loss of <em>function</em>, not by frightening sensations. That said, no guide can examine you, so if you're worried or have any of the red-flag signs below, a single check with a doctor is completely reasonable and can lift an enormous weight.</p></div>
            </details>
            <details>
              <summary>Why can I feel it so strongly but no one can see anything?</summary>
              <div className="iv-a"><p>Because it's a sensation generated and amplified inside an over-alert nervous system, rather than a large visible movement. The very fact that it's strongly felt but not visible tends to point toward the common, benign explanation rather than the serious ones people fear — though only a doctor can speak to your specific case.</p></div>
            </details>
            <details>
              <summary>Will it ever go away?</summary>
              <div className="iv-a"><p>For most people it eases considerably as the underlying drivers — stress, poor sleep, stimulants, and the worry loop — settle down. It may come and go rather than vanish overnight, but "loud and constant" very often becomes "faint and occasional," and then something you rarely think about.</p></div>
            </details>
            <details>
              <summary>Is it dangerous to just leave it?</summary>
              <div className="iv-a"><p>A benign internal vibration isn't harming your body. The thing most worth addressing is usually the anxiety around it, because that's what causes the suffering and keeps the loop spinning. If you're ever unsure whether what you have is benign, that uncertainty itself is a good reason to get one reassuring check.</p></div>
            </details>
            <details>
              <summary>Why is it worst when I'm trying to sleep?</summary>
              <div className="iv-a"><p>Because stillness removes the distractions that were crowding the sensation out during the day, and because a resting mind drifts toward what it fears, which spins up the worry loop at exactly the wrong moment. It's one of the most common — and most reassuringly benign — patterns we hear about.</p></div>
            </details>
          </div>
        </section>

        <section>
          <div className="iv-flag">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
              When to check in with a doctor
            </h3>
            <p style={{ margin: 0 }}>
              Internal vibrations on their own are usually benign. But sensation is only half the
              picture — the other half is <em>function</em>. It's worth arranging a medical assessment
              if the buzzing comes alongside any of these:
            </p>
            <ul>
              <li>Genuine muscle <strong>weakness</strong> — things slipping from your grip, a foot dragging, real difficulty on stairs</li>
              <li>Visible muscle <strong>shrinking or wasting</strong> in a particular area</li>
              <li>New or worsening trouble <strong>swallowing or speaking</strong></li>
              <li>Numbness, vision changes, or any symptom that is clearly and steadily <strong>getting worse over time</strong></li>
              <li>Anything that simply frightens you enough that reassurance from a professional would help</li>
            </ul>
            <p style={{ margin: '14px 0 0' }}>
              Seeking one evaluation for peace of mind is completely reasonable, and a good doctor will
              understand. This guide exists to inform you and to calm you — never to talk you out of
              getting checked.
            </p>
          </div>
        </section>

        <hr className="iv-rule" />

        <div className="iv-closer">
          <p className="iv-serif iv-closer-h">If you take one thing from this</p>
          <p>
            A buzzing you can feel but not see, that arrives at rest and eases when you're absorbed in
            life, that waxes and wanes with your stress and your sleep — this is one of the most common,
            and most benign, experiences we hear about. It is your nervous system asking for calm, not
            warning you of catastrophe. Meet it with steadiness rather than alarm, give the small habits
            real time, loosen the grip of checking, and it very often grows quiet.
          </p>
          <p>
            You've already done something meaningful by reading to the end of this instead of down
            another rabbit hole of worry. That's the direction of getting better.
          </p>
          <p className="iv-sign">— The BFS Support Team</p>
        </div>

        <div className="iv-next">
          <h3>Still have a question on your mind?</h3>
          <p>Our BFS assistant is here any time — ask anything, and browse more guides whenever you're ready.</p>
          <a className="iv-btn" href="/resources">Explore more resources</a>
        </div>

        <p className="iv-disclaimer">
          This guide is for information and education only and is not a substitute for professional
          medical advice, diagnosis, or treatment. It is not medical advice. Always seek the guidance of
          your physician or another qualified health provider with any questions you may have about a
          medical condition, and never disregard professional medical advice or delay seeking it because
          of something you have read here.
        </p>
      </div>
    </div>
  );
}

export default InternalVibrationsGuide;
